import { IoSearchOutline } from 'react-icons/io5';
import { FiDownload } from 'react-icons/fi';
import React, { useContext, useEffect, useState } from 'react';
import Button from '../../components/shared/custom-btn';
import Heading from '../../components/shared/heading';
import Modal from '../../components/shared/modal';
import AddModal from './add-modal';
import UserTable from './user-table';
import { AccountsContext } from '../../components/context/account-context';
import { getAllUserRoles, getAllUsers, getAllUserStatuses } from '../../services/users-service';
import { CSVLink } from 'react-csv';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logo from '../../assets/images/logo.jpg';
import Help from './help';
import { usePageHelp } from '../../components/context/help-context';

const Users = () => {
    usePageHelp(Help);
    const { accountId } = useContext(AccountsContext);
    const [searchTerm, setSearchTerm] = useState('');
    const [openAddModal, setOpenAddModal] = useState(false);
    const [newlyAddedItem, setNewlyAddedItem] = useState(null);
    const [userData, setUserData] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [statusOptions, setStatusOptions] = useState([]);
    const [roles, setRoles] = useState([]);
    const [selectedRows, setSelectedRows] = useState({});
    const [loadingGlobalData, setLoadingGlobalData] = useState(false);

    const fetchGlobalData = async () => {
        if (!accountId) return;
        setLoadingGlobalData(true);

        try {
            const [statusOptionsRes, rolesRes] = await Promise.all([
                getAllUserStatuses(accountId),
                getAllUserRoles(accountId),
            ]);

            if (statusOptionsRes.success) setStatusOptions(statusOptionsRes.data.data || []);
            if (rolesRes.success) setRoles(rolesRes.data.data || []);
        } catch (err) {
            console.error("Failed to fetch global data", err);
            setError('Failed to fetch global data');
        } finally {
            setLoadingGlobalData(false);
        }
    };

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await getAllUsers(accountId);
            console.log(response)
            if (response.success) {
                const sortedData = response.data.data.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn)); // sort latest first
                setUserData(sortedData || []);
                setError(null);
            } else {
                setError(response.data.message || 'Failed to fetch users records');
            }
        } catch (err) {
            setError('An error occurred while fetching user records');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [accountId]);

    const handleOpenAddModal = () => {
        setOpenAddModal(true);
    };

    const handleCloseAddModal = () => {
        setOpenAddModal(false);
    };

    const handleAddItem = (newItem) => {
        setNewlyAddedItem(newItem);
        fetchUsers();
    };

    const handleOpenEditModal = (item) => {
        setNewlyAddedItem(item);
    };

    const handleClearNewlyAddedItem = () => {
        setNewlyAddedItem(null);
    };

    // Filter data for export based on selected rows
    const exportData = Object.keys(selectedRows).length > 0
        ? userData.filter((_, index) => selectedRows[index])
        : userData;

    const csvData = exportData.map(item => ({
        'User ID': item.userId,
        Name: `${item.firstName || ''} ${item.lastName || ''}`.trim(),
        Email: item.email,
        Role: item.role,
        Status: item.status,
    }));

    const handleExportPDF = () => {
        const doc = new jsPDF();

        // Logo placement
        const logoWidth = 40;
        const logoHeight = 30;
        const pageWidth = doc.internal.pageSize.getWidth();
        const logoX = (pageWidth - logoWidth) / 2;
        const logoY = 2;
        doc.addImage(logo, 'PNG', logoX, logoY, logoWidth, logoHeight);

        // Title placement
        const title = 'Users Report';
        const titleY = logoY + logoHeight + 5;
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        const titleWidth = doc.getTextWidth(title);
        const titleX = (pageWidth - titleWidth) / 2;
        doc.text(title, titleX, titleY);

        // Table
        autoTable(doc, {
            head: [['User ID', 'Name', 'Email', 'Role', 'Status']],
            body: exportData.map(item => [
                item.userId || '',
                `${item.firstName || ''} ${item.lastName || ''}`.trim(),
                item.email || '',
                item.role || '',
                item.status || '',
            ]),
            startY: titleY + 5,
        });

        // Save PDF
        doc.save('users_list.pdf');
    };

    const isExportDisabled = Object.keys(selectedRows).length === 0;

    return (
        <div className="px-6 py-4">
            <div className='flex mb-2 items-center justify-between'>
                <div>
                    <Heading>Users</Heading>
                </div>
                <Button
                    label='Add User'
                    onClick={handleOpenAddModal}
                    className='text-white px-4 !py-1 rounded-lg bg-btnBg'
                />
            </div>

            <div className='space-y-4'>
                {error && (
                    <div className="text-red-600 bg-red-100 px-4 py-2 rounded-md">
                        {error}
                    </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex gap-3 flex-wrap">
                        <div className="bg-white rounded-md px-3 flex border border-border items-center gap-2 w-44 lg:w-72 py-1">
                            <IoSearchOutline className="text-gray-500 text-lg" />
                            <input
                                type="text"
                                className="bg-transparent outline-none py-1 w-full text-sm text-gray-700"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="flex gap-2 items-center">
                        <Button
                            onClick={handleExportPDF}
                            className={` ${isExportDisabled ? '!cursor-not-allowed !opacity-75' : 'hover:!bg-gray-50'} flex items-center gap-1 !text-sm !px-3 !py-1.5 border rounded-md `}
                            label="PDF"
                            icon={<FiDownload className="text-gray-600" />}
                            iconPosition='left'
                            disabled={isExportDisabled}
                        />
                        <div
                            className={`flex items-center gap-1 !text-sm !px-3 !py-1.5 border rounded-md ${isExportDisabled ? 'cursor-not-allowed opacity-75' : 'hover:bg-gray-50'}`}
                        >
                            <CSVLink
                                data={csvData}
                                onClick={(event) => {
                                    if (isExportDisabled) {
                                        event.preventDefault();
                                    }
                                }}
                                filename="users_list.csv"
                                className={`${isExportDisabled ? 'cursor-not-allowed' : ''} flex items-center gap-1`}
                            >
                                <FiDownload className="text-gray-600" />
                                CSV
                            </CSVLink>
                        </div>
                    </div>
                </div>

                <UserTable
                    data={userData}
                    setData={setUserData}
                    globalFilter={searchTerm}
                    onGlobalFilterChange={setSearchTerm}
                    newlyAddedItem={newlyAddedItem}
                    setNewlyAddedItem={setNewlyAddedItem}
                    onClearNewlyAddedItem={handleClearNewlyAddedItem}
                    fetchUsers={fetchUsers}
                    loadingData={loading}
                    accountId={accountId}
                    statusOptions={statusOptions}
                    setSelectedRows={setSelectedRows}
                    selectedRows={selectedRows}
                    roles={roles}
                    fetchGlobalData={fetchGlobalData}
                    loadingGlobalData={loadingGlobalData}
                />

                <Modal
                    isOpen={openAddModal}
                    onClose={handleCloseAddModal}
                    modalClass={'xl:w-[40%] lg:w-[45%] md:w-[65%] sm:w-[70%] w-[90%] !px-0'}
                >
                    <AddModal
                        handleCloseAddModal={handleCloseAddModal}
                        onAdd={handleAddItem}
                        openEditModal={handleOpenEditModal}
                        accountId={accountId}
                        roles={roles}
                        fetchGlobalData={fetchGlobalData}
                        loadingGlobalData={loadingGlobalData}
                    />
                </Modal>
            </div>
        </div>
    );
};

export default Users;